import { useEffect, useRef, useState, type ChangeEvent, type SyntheticEvent } from 'react';
import { Box, Collapse, Grid, LinearProgress, Stack, Tab, Tabs } from '@mui/material';
import { Button, Icon, Input, Switch, Typography, Dialog } from '@sm-dashboard/ui-components';
import instanceAxios from '@/util/axios';
import { feedBannersUrl, uploadFileUrl } from '@/constants';
import { useAppContext } from '@/context/context';
import { FeedBanner } from './FeedBannersCarousel';

type FeedBannersDialogProps = {
  open: boolean;
  onClose: () => void;
  onBannersChange?: () => void;
};

const emptyForm = {
  name: '',
  fileUrl: '',
  fileType: 'image' as FeedBanner['fileType'],
  isActive: true,
  link: '',
  address: ''
};

const uploadFolder =
  (import.meta as unknown as { env: { VITE_ENVIRONMENT?: string } }).env.VITE_ENVIRONMENT === 'DEV'
    ? 'feed-banners-staging'
    : 'feed-banners';

const bannerToForm = (banner: FeedBanner) => ({
  name: banner.name,
  fileUrl: banner.fileUrl,
  fileType: banner.fileType,
  isActive: banner.isActive,
  link: banner.link ?? '',
  address: banner.address ?? ''
});

export const FeedBannersDialog = ({ open, onClose, onBannersChange }: FeedBannersDialogProps) => {
  const { setSnackbar } = useAppContext();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [tab, setTab] = useState(0);
  const [banners, setBanners] = useState<FeedBanner[]>([]);
  const [loading, setLoading] = useState(false);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [selectedBannerId, setSelectedBannerId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);

  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const [bannerToDelete, setBannerToDelete] = useState<FeedBanner | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const response = await instanceAxios.get<FeedBanner[]>(feedBannersUrl, { params: { all: 'true' } });
      setBanners(response.data || []);
    } catch {
      setBanners([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;

    setTab(0);
    setEditingId(null);
    setSelectedBannerId(null);
    setForm(emptyForm);
    setUploadError(null);
    setBannerToDelete(null);
    setUploading(false);
    setSaving(false);
    fetchBanners();
  }, [open]);

  const resetCreation = () => {
    setEditingId(null);
    setSelectedBannerId(null);
    setForm(emptyForm);
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setTab(newValue);
    if (newValue === 0) {
      resetCreation();
    }
  };

  const handleSelectBanner = (id: number) => {
    if (selectedBannerId === id) {
      setSelectedBannerId(null);
      setEditingId(null);
      setForm(emptyForm);
      setUploadError(null);
      return;
    }

    const banner = banners.find(b => b.id === id);
    if (!banner) return;

    setSelectedBannerId(id);
    setEditingId(id);
    setForm(bannerToForm(banner));
    setUploadError(null);
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadError(null);
    setUploading(true);
    const fileType: FeedBanner['fileType'] = file.type.startsWith('video/') ? 'video' : 'image';
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await instanceAxios.post<{ publicUrl: string }>(
        `${uploadFileUrl}?folder=${uploadFolder}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setForm(prev => ({ ...prev, fileUrl: data.publicUrl, fileType }));
    } catch {
      setUploadError('שגיאה בהעלאת הקובץ. נסה שנית.');
    } finally {
      setUploading(false);
    }
  };

  const canSave = Boolean(form.name.trim() && form.fileUrl);

  const handleSave = async () => {
    if (!canSave) return;
    if (tab === 1 && !editingId) return;

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        fileUrl: form.fileUrl,
        fileType: form.fileType,
        isActive: form.isActive,
        link: form.link.trim() || null,
        address: form.address.trim() || null
      };

      if (editingId) {
        await instanceAxios.put(`${feedBannersUrl}/${editingId}`, payload);
        setSnackbar({ message: 'הבאנר עודכן בהצלחה', severity: 'success', open: true });
      } else {
        await instanceAxios.post(feedBannersUrl, payload);
        setSnackbar({ message: 'הבאנר נוסף בהצלחה', severity: 'success', open: true });
      }

      onBannersChange?.();
      resetCreation();
      await fetchBanners();
      onClose();
    } catch {
      setSnackbar({ message: 'שגיאה בשמירת הבאנר', severity: 'error', open: true });
    } finally {
      setSaving(false);
    }
  };

  const formFields = (
    <Grid container spacing={3} sx={{ mt: 1 }}>
      <Grid item xs={12}>
        <Input
          appearance="plain"
          label="שם הבאנר"
          placeholder="הזן שם לבאנר"
          value={form.name}
          onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
          fullWidth
          required
        />
      </Grid>

      <Grid item xs={12}>
        <Stack direction="row" spacing={2} alignItems="flex-end">
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Input
              appearance="plain"
              label="לינק"
              placeholder="https://example.com"
              value={form.link}
              onChange={e => setForm(prev => ({ ...prev, link: e.target.value }))}
              fullWidth
            />
          </Box>
          <Box sx={{ flexShrink: 0, pb: 0.5 }}>
            <Switch
              label={form.isActive ? 'פעיל' : 'כבוי'}
              checked={form.isActive}
              onChange={(_, checked) => setForm(prev => ({ ...prev, isActive: checked }))}
            />
          </Box>
        </Stack>
      </Grid>

      <Grid item xs={12}>
        <Box
          onClick={() => !uploading && fileInputRef.current?.click()}
          sx={{
            width: '100%',
            minHeight: 140,
            borderRadius: 3,
            border: '2px dashed',
            borderColor: form.fileUrl ? 'primary.main' : 'primary.light',
            bgcolor: 'grey.50',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' }
          }}
        >
          {form.fileUrl ? (
            form.fileType === 'video' ? (
              <Box
                component="video"
                src={form.fileUrl}
                muted
                controls
                sx={{ width: '100%', maxHeight: 180, objectFit: 'contain' }}
              />
            ) : (
              <Box component="img" src={form.fileUrl} alt="" sx={{ width: '100%', maxHeight: 180, objectFit: 'contain' }} />
            )
          ) : (
            <Stack alignItems="center" gap={1} sx={{ px: 2, textAlign: 'center', py: 2 }}>
              <Icon icon="image" color="primary.main" size="large" />
              <Typography variant="body2">לחץ להעלאת תמונה, גיף או וידאו</Typography>
              <Typography variant="caption" color="text.secondary">
                JPEG · PNG · GIF · MP4
              </Typography>
            </Stack>
          )}

          {uploading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'rgba(255,255,255,0.7)',
                display: 'flex',
                alignItems: 'flex-end'
              }}
            >
              <LinearProgress sx={{ width: '100%' }} />
            </Box>
          )}
        </Box>

        <Box
          component="input"
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          onChange={handleFileChange}
          sx={{ display: 'none' }}
        />

        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            גודל מומלץ: 1600 × 600 px (יחס 16:6). לא להניח טקסט או לוגו בקצוות — הם עלולים להיחתך.
          </Typography>
        </Box>

        {uploadError && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="error.main">
              {uploadError}
            </Typography>
          </Box>
        )}
      </Grid>
    </Grid>
  );

  const handleConfirmDelete = async () => {
    if (!bannerToDelete) return;

    setDeleting(true);
    try {
      await instanceAxios.delete(`${feedBannersUrl}/${bannerToDelete.id}`);
      setSnackbar({ message: 'הבאנר נמחק בהצלחה', severity: 'success', open: true });
      await fetchBanners();
      onBannersChange?.();
      onClose();
    } catch {
      setSnackbar({ message: 'שגיאה במחיקת הבאנר', severity: 'error', open: true });
    } finally {
      setDeleting(false);
      setBannerToDelete(null);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        title="ניהול באנרים"
        cancelAction={
          tab === 1
            ? {
                onClick: () => {
                  setTab(0);
                  resetCreation();
                }
              }
            : undefined
        }
        secondaryActions={tab === 1 ? undefined : [{ label: 'ניקוי', onClick: resetCreation }]}
        primaryAction={{
          label: tab === 1 ? 'שמירת שינויים' : 'הוספת באנר',
          onClick: handleSave,
          loading: saving,
          disabled: uploading || saving || !canSave || (tab === 1 && !editingId)
        }}
      >
        <Tabs value={tab} onChange={handleTabChange} variant="fullWidth">
          <Tab value={0} label="יצירה" />
          <Tab value={1} label="באנרים קיימים" />
        </Tabs>

        <Box sx={{ mt: 2 }}>
          {tab === 1 && (
            <Stack spacing={1.5}>
              {loading ? (
                <Typography variant="body2">טוען באנרים...</Typography>
              ) : banners.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  אין באנרים עדיין
                </Typography>
              ) : (
                banners.map(banner => {
                  const isSelected = banner.id === selectedBannerId;
                  return (
                    <Box
                      key={banner.id}
                      sx={{
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        bgcolor: isSelected ? 'action.hover' : 'background.paper',
                        overflow: 'hidden'
                      }}
                    >
                      <Box
                        onClick={() => handleSelectBanner(banner.id)}
                        sx={{
                          cursor: 'pointer',
                          px: 1.5,
                          py: 1.25,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5
                        }}
                      >
                        <Box
                          sx={{
                            width: 96,
                            height: 56,
                            borderRadius: 1.5,
                            overflow: 'hidden',
                            flexShrink: 0,
                            bgcolor: 'grey.100',
                            border: '1px solid',
                            borderColor: 'divider'
                          }}
                        >
                          {banner.fileType === 'video' ? (
                            <Box
                              component="video"
                              src={banner.fileUrl}
                              muted
                              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            <Box
                              component="img"
                              src={banner.fileUrl}
                              alt=""
                              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          )}
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            <Typography variant="body2">{banner.name}</Typography>
                          </Box>
                          <Typography variant="caption" color={banner.isActive ? 'success.main' : 'text.secondary'}>
                            {banner.isActive ? 'פעיל' : 'כבוי'}
                          </Typography>
                        </Box>

                        <Box onClick={e => e.stopPropagation()} sx={{ flexShrink: 0 }}>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            fullWidth={false}
                            onClick={() => setBannerToDelete(banner)}
                          >
                            מחיקה
                          </Button>
                        </Box>
                      </Box>

                      <Collapse in={isSelected} unmountOnExit>
                        <Box
                          sx={{
                            px: 1.5,
                            pb: 2,
                            pt: 0.5,
                            borderTop: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'background.paper'
                          }}
                        >
                          {formFields}
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })
              )}
            </Stack>
          )}

          {tab === 0 && formFields}
        </Box>
      </Dialog>

      <Dialog
        open={Boolean(bannerToDelete)}
        onClose={() => setBannerToDelete(null)}
        maxWidth="xs"
        fullWidth
        title="אישור מחיקה"
        cancelAction
        primaryAction={{ label: 'מחק', onClick: handleConfirmDelete, loading: deleting, color: 'error' }}
      >
        <Typography variant="body2">האם למחוק את הבאנר {bannerToDelete?.name}?</Typography>
      </Dialog>
    </>
  );
};

